/* eslint-disable eqeqeq */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FiShare, FiDownload } from "react-icons/fi";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import ReusableTable from "./OnBoardingAudit/ReusableTable";
import { tableConfigs } from "./OnBoardingAudit/tableConfigurations";

const OnboardingAudit = () => {
  const navigate = useNavigate();

  // --- State ---
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedOption, setSelectedOption] = useState("last1year");
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // --- Helpers ---
  const getStartDate = (option, project) => {
    const today = new Date();

    if (option === "last1year") {
      return new Date(today.getFullYear() - 1, today.getMonth(), 1);
    }
    if (option === "last2years") {
      return new Date(today.getFullYear() - 2, today.getMonth(), 1);
    }
    if (option === "last3years") {
      return new Date(today.getFullYear() - 3, today.getMonth(), 1);
    }

    if (option === "allTime") {
      if (project && project.campaign_start_date) {
        return new Date(project.campaign_start_date);
      }
      return new Date("2020-01-01");
    }

    return new Date(today.getFullYear() - 1, today.getMonth(), 1);
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: getStartDate("last1year", null),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  // --- Handlers ---
  const handleSelectChange = (event) => {
    const newOption = event.target.value;
    setSelectedOption(newOption);

    setDateRange([
      {
        ...dateRange[0],
        startDate: getStartDate(newOption, selectedProject),
      },
    ]);
  };

  const handleProjectSelect = (projectId) => {
    if (projectId === "create_new") {
      navigate("/projects");
      setIsProjectDropdownOpen(false);
      return;
    }

    const project = projects.find((p) => p.id === projectId);

    if (!project || project.customer_id == null) return;

    setSelectedProject(project);
    setIsProjectDropdownOpen(false);

    setDateRange([
      {
        ...dateRange[0],
        startDate: getStartDate(selectedOption, project),
      },
    ]);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await fetch(
          "https://api.confidanto.com/projects-data/project-list",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: localStorage.getItem("email") }),
          }
        );

        const rawData = await response.json();

        const data = rawData.map((p) => {
          const defaultAccount =
            p.ad_accounts && p.ad_accounts.length > 0 ? p.ad_accounts[0] : null;

          return {
            ...p,
            id: p.project_id,
            customer_id: defaultAccount ? defaultAccount.customer_id : null,
            campaign_start_date: defaultAccount
              ? defaultAccount.campaign_start_date
              : null,
          };
        });

        if (Array.isArray(data) && data.length > 0) {
          setProjects(data);

          const storedProjectId = localStorage.getItem("project_id");
          const storedCustomerId = localStorage.getItem("customer_id");

          let projectToSelect = data[0];

          if (storedProjectId && storedCustomerId) {
            const foundProject = data.find((p) => {
              const isIdMatch = p.id == storedProjectId;
              const isCustomerMatch = p.ad_accounts?.some(
                (acc) => acc.customer_id == storedCustomerId
              );
              return isIdMatch && isCustomerMatch;
            });

            if (foundProject) {
              const specificAccount = foundProject.ad_accounts.find(
                (acc) => acc.customer_id == storedCustomerId
              );
              projectToSelect = {
                ...foundProject,
                customer_id: specificAccount
                  ? specificAccount.customer_id
                  : foundProject.customer_id,
                campaign_start_date: specificAccount
                  ? specificAccount.campaign_start_date
                  : foundProject.campaign_start_date,
              };
            }
          }

          if (projectToSelect.customer_id == null) {
            projectToSelect = data.find((p) => p.customer_id != null) || null;
          }

          setSelectedProject(projectToSelect);

          if (projectToSelect) {
            setDateRange((prev) => [
              {
                ...prev[0],
                startDate: getStartDate(selectedOption, projectToSelect),
              },
            ]);
          }
        } else {
          setProjects([]);
          setSelectedProject(null);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [selectedOption]); // Added selectedOption dependency if needed, otherwise empty array

  const orderedTableConfigs = useMemo(() => {
    const bestMonthsTable = tableConfigs.find((c) => c.title === "Best Months");
    const pastYearTable = tableConfigs.find(
      (c) => c.title === "Past Year Data"
    );
    const otherTables = tableConfigs.filter(
      (c) => c.title !== "Best Months" && c.title !== "Past Year Data"
    );
    const newList = [...otherTables];

    if (selectedOption === "last1year") {
      if (bestMonthsTable) newList.splice(0, 0, bestMonthsTable);
      if (pastYearTable) newList.splice(1, 0, pastYearTable);
    } else {
      if (pastYearTable) newList.splice(0, 0, pastYearTable);
      if (bestMonthsTable) newList.splice(1, 0, bestMonthsTable);
    }

    return newList;
  }, [selectedOption]);

  return (
    <>
      <div className="flex justify-between items-center mx-10 my-6">
        <div className="flex items-center space-x-4">
          
          {/* --- Project Selector Dropdown --- */}
          {loadingProjects ? (
            <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 shadow-sm min-w-[200px]">
              Loading...
            </div>
          ) : projects.length > 0 ? (
            <div className="relative min-w-[200px]" ref={dropdownRef}>
              <button
                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
                className="w-full flex justify-between items-center px-4 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-left transition-colors cursor-pointer"
              >
                <span className="truncate pr-4">
                  {selectedProject ? selectedProject.name : "Select Project"}
                </span>
                <FaChevronDown
                  className={`w-3 h-3 text-gray-500 transition-transform ${
                    isProjectDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu List */}
              {isProjectDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="max-h-60 overflow-y-auto">
                    {projects.map((project) => {
                      const isSelected = selectedProject?.id === project.id;
                      const isDisabled = project.customer_id == null;

                      return (
                        <div
                          key={project.id}
                          onClick={() => {
                            if (!isDisabled) handleProjectSelect(project.id);
                          }}
                          className={`px-4 py-2 text-sm transition-colors border-b border-gray-50 last:border-0 
                            ${
                              isSelected
                                ? "bg-blue-50 font-medium text-blue-700 cursor-default"
                                : isDisabled
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "text-gray-700 hover:bg-blue-50 cursor-pointer"
                            }
                          `}
                          title={isDisabled ? "No ad account connected" : ""}
                        >
                          {project.name}
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-gray-200">
                    <div
                      onClick={() => handleProjectSelect("create_new")}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-50 text-blue-600 font-semibold text-sm transition-colors"
                    >
                      + Create New Project
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate("/projects")}
              className="px-4 py-2 border border-blue-500 text-blue-600 text-sm font-medium rounded-lg bg-white hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              + Create Project
            </button>
          )}

          {/* --- Date Range Selector Dropdown --- */}
          <div className="relative min-w-[140px]">
            <select
              value={selectedOption}
              onChange={handleSelectChange}
              className="appearance-none w-full px-4 py-2 pr-10 border border-gray-200 rounded-lg bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer transition-colors"
            >
              <option value="last1year">Last 1 year</option>
              <option value="last2years">Last 2 years</option>
              <option value="last3years">Last 3 years</option>
              <option value="allTime">All Time</option>
            </select>
            {/* Custom chevron to override the native select arrow */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
              <FaChevronDown className="w-3 h-3" />
            </div>
          </div>

        </div>

        {/* --- Hidden Action Buttons --- */}
        <div className="hidden items-center space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            <FiShare className="text-gray-500" />
            <span>Share</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            <FiDownload className="text-gray-500" />
            <span>Download</span>
          </button>
        </div>
      </div>

      {orderedTableConfigs.map((config) => (
        <ReusableTable
          key={config.title}
          dateRange={dateRange}
          title={config.title}
          apiEndpoint={config.apiEndpoint}
          columnsConfig={config.columnsConfig}
          apiRequestBodyExtras={config.apiRequestBodyExtras}
          projectId={selectedProject?.id}
          customerId={selectedProject?.customer_id}
          selectedProject={selectedProject}
        />
      ))}
    </>
  );
};

export default OnboardingAudit;
